namespace Client.Components.Select

open Client.DomainTypes.PhysicalSensorList
open Client.DomainTypes.Select.UserFilter
open Fulma
open Fable.React
open Fable.React.Props
open Shared
open Shared.Dto
open Shared.Dto.Form
open Shared.Dto.RimPro
open Shared.Dto.User
open Shared.DtoTypes.DeviceLocation
open Shared.DtoTypes.PhysicalSensor
open Shared.Infrastructure

[<RequireQualifiedAccess>]
module UserSelect =
    let userToOption (user: UserDto) =
        option [ Value user.Id ] [ str (getFullNameLastFirst user) ]

    let view (onUserSelected: UserDto option -> unit) (selectedUser: UserDto option) (list: UserDto list) =
        let options = option [ Value "" ] [ str "-" ] :: List.map userToOption list

        let findUser =
            fun (maybeId: string) ->
                Int.tryParse maybeId
                |> Option.map (fun userId -> List.tryFind (fun (user: UserDto) -> user.Id = userId) list)
                |> Option.flatten


        let selectedValue =
            Option.map (fun (user: UserDto) -> user.Id.ToString()) selectedUser
            |> Option.defaultValue ""

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue selectedValue
                    OnChange(fun event -> event.Value |> findUser |> onUserSelected)
                ]
                options
        ]

[<RequireQualifiedAccess>]
module UserFilter =
    let private selectionToState (users: UserDto list) (selection: string) =
        match selection with
        | "none" -> NoFilter
        | "without_user" -> WithoutUser
        | id ->
            id
            |> Int.tryParse
            |> Option.bind (fun id -> List.tryFind (fun (user: UserDto) -> user.Id = id) users)
            |> Option.map WithUser
            |> Option.defaultValue NoFilter

    let view (onUserSelected: State -> unit) (selectedItem: State) (users: UserDto list) =
        let options = [
            option [ Value "none" ] [ str "-" ]
            option [ Value "without_user" ] [ str "(Ohne Benutzer)" ]
            yield! List.map UserSelect.userToOption users
        ]

        let selectedValue =
            match selectedItem with
            | NoFilter -> "none"
            | WithoutUser -> "without_user"
            | WithUser user -> user.Id.ToString()

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue selectedValue
                    OnChange(fun event -> event.Value |> selectionToState users |> onUserSelected)
                ]
                options
        ]

    let applyFilter (userId: int option) (state: State) : bool =
        match state with
        | NoFilter -> true
        | WithoutUser -> userId.IsNone
        | WithUser user -> userId = Some(user.Id)

module DeviceConditionSelect =
    let internal conditionToId (condition: DeviceCondition) =
        match condition with
        | Operational -> "operational"
        | Defective -> "defective"
        | Repaired -> "repaired"

    let private conditionToOption (condition: DeviceCondition) =
        option [ Value(conditionToId condition) ] [ str (DeviceCondition.label condition) ]

    let internal idToCondition (id: string) =
        match id with
        | "defective" -> Defective
        | "repaired" -> Repaired
        | _ -> Operational

    let view (onConditionSelected: DeviceCondition -> unit) (selectedCondition: DeviceCondition) =
        let options =
            [
                DeviceCondition.Operational
                DeviceCondition.Defective
                DeviceCondition.Repaired
            ]
            |> List.map conditionToOption

        let selectedValue = conditionToId selectedCondition

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue selectedValue
                    OnChange(fun event -> event.Value |> idToCondition |> onConditionSelected)
                ]
                options
        ]

module DeviceConditionFilter =
    let private conditionToId (condition: DeviceCondition option) =
        condition
        |> Option.map DeviceConditionSelect.conditionToId
        |> Option.defaultValue ""

    let private label (condition: DeviceCondition option) =
        condition |> Option.map DeviceCondition.label |> Option.defaultValue "-"

    let private conditionToOption (condition: DeviceCondition option) =
        option [ Value(conditionToId condition) ] [ str (label condition) ]

    let private idToCondition (maybeId: string) =
        match maybeId with
        | "" -> None
        | id -> DeviceConditionSelect.idToCondition id |> Some

    let view (onConditionSelected: DeviceCondition option -> unit) (selectedCondition: DeviceCondition option) =
        let options =
            [
                None
                Some DeviceCondition.Operational
                Some DeviceCondition.Defective
                Some DeviceCondition.Repaired
            ]
            |> List.map conditionToOption

        let selectedValue = conditionToId selectedCondition

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue selectedValue
                    OnChange(fun event -> event.Value |> idToCondition |> onConditionSelected)
                ]
                options
        ]

module DeviceLocationSelect =
    let private locationToOption (location: DeviceLocation) =
        option [ Value(location.Id) ] [ str location.Name ]

    let private locationFromEvent (locations: DeviceLocation list) (id: string) =
        id
        |> Int.tryParse
        |> Option.bind (fun id -> List.tryFind (fun (location: DeviceLocation) -> location.Id = id) locations)
        |> Option.defaultValue locations[0]

    let view
        (locations: DeviceLocation list)
        (onConditionSelected: DeviceLocation -> unit)
        (selectedLocation: DeviceLocation)
        =
        let options = List.map locationToOption locations

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue selectedLocation.Id
                    OnChange(fun event -> event.Value |> locationFromEvent locations |> onConditionSelected)
                ]
                options
        ]

module DeviceLocationFilter =
    let private locationToOption (location: DeviceLocation) =
        option [ Value(location.Id) ] [ str location.Name ]

    let private selectionToId (selectedLocation: DeviceLocation option) =
        match selectedLocation with
        | Some location -> location.Id.ToString()
        | None -> ""

    let private locationFromId (locations: DeviceLocation list) (id: string) =
        id
        |> Int.tryParse
        |> Option.bind (fun id -> List.tryFind (fun (location: DeviceLocation) -> location.Id = id) locations)
        |> Option.defaultValue locations[0]

    let private selectionToLocation (locations: DeviceLocation list) (selection: string) =
        match selection with
        | "" -> None
        | id -> locationFromId locations id |> Some

    let view
        (locations: DeviceLocation list)
        (onConditionSelected: DeviceLocation option -> unit)
        (selectedLocation: DeviceLocation option)
        =
        let options = [
            option [ Value "" ] [ str "-" ]

            yield! List.map locationToOption locations
        ]

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(selectionToId selectedLocation)
                    OnChange(fun event -> event.Value |> selectionToLocation locations |> onConditionSelected)
                ]
                options
        ]

[<RequireQualifiedAccess>]
module PressureProbeSelect =
    let private probeToLabel (probe: PressureProbeOption) =
        match probe with
        | PressureProbeOption.WaterDepth -> "Wassertiefe"
        | PressureProbeOption.Pressure -> "Druck"

    let private probeFromLabel (label: string) =
        match label with
        | "Druck" -> PressureProbeOption.Pressure
        | _ -> PressureProbeOption.WaterDepth


    let private probeToOption (probe: PressureProbeOption) =
        option [ Value(probeToLabel probe) ] [ str (probeToLabel probe) ]

    let view (onConditionSelected: PressureProbeOption -> unit) (selectedLocation: PressureProbeOption) =
        let availableProbes = [
            PressureProbeOption.WaterDepth
            PressureProbeOption.Pressure
        ]

        let options = List.map probeToOption availableProbes

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(probeToLabel selectedLocation)
                    OnChange(fun event -> event.Value |> probeFromLabel |> onConditionSelected)
                ]
                options
        ]

[<RequireQualifiedAccess>]
module SensorSelect =
    let private sensorToOption (sensor: SelectableSensor) =
        option [ Value sensor.Id ] [ str sensor.Name ]

    let private toOptions (sensors: SelectableSensors) =
        let userSensors = sensors.MySensors |> List.map sensorToOption

        match sensors.GroupSensors with
        | [] -> userSensors
        | groupSensors ->
            let groupSensors = groupSensors |> List.map sensorToOption

            let group = optgroup [ HTMLAttr.Label "Gruppen-Sensoren" ] groupSensors

            [ yield! userSensors; group ]

    let view
        (onSensorSelected: SelectableSensor option -> unit)
        (selected: SelectableSensor option)
        (availableSensors: SelectableSensors)
        (withEmpty: bool)
        =
        let sensorOptions = toOptions availableSensors

        let options =
            if withEmpty then
                option [ Value "" ] [ str "-" ] :: sensorOptions
            else
                sensorOptions

        let findSensor id =
            availableSensors |> SelectableSensors.tryFind id

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(selected |> Option.map (fun x -> x.Id |> string) |> Option.defaultValue "")
                    OnChange(fun event -> (event.Value |> Int.tryParse |> Option.bind findSensor |> onSensorSelected))
                ]
                options
        ]

[<RequireQualifiedAccess>]
module TalgilProgramSelect =
    let private toOption (program: TalgilTriggerForm.ProgramDto) =
        option [ Value program.Id ] [ str program.Name ]

    let view
        (onProgramSelected: TalgilTriggerForm.ProgramDto option -> unit)
        (selectedProgram: TalgilTriggerForm.ProgramDto option)
        (list: TalgilTriggerForm.ProgramDto list)
        =
        let options = List.map toOption list

        let findProgram =
            fun (maybeId: string) ->
                maybeId
                |> Int.tryParse
                |> Option.bind (fun id ->
                    List.tryFind (fun (program: TalgilTriggerForm.ProgramDto) -> program.Id = id) list
                )

        let selectedValue =
            Option.map (fun (program: TalgilTriggerForm.ProgramDto) -> program.Id) selectedProgram
            |> Option.map string
            |> Option.defaultValue ""

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue selectedValue
                    OnChange(fun event -> event.Value |> findProgram |> onProgramSelected)
                ]
                options
        ]

[<RequireQualifiedAccess>]
module MapSensorSelect =
    let private sensorToOption (sensor: IdValue<MapSensorDto>) =
        let baseData = MapSensor.getBaseData sensor.Value
        let string = baseData.Name

        option [ Value baseData.Name ] [ str string ]

    let view
        (onSensorSelected: IdValue<MapSensorDto> option -> unit)
        (selected: IdValue<MapSensorDto> option)
        (availableSensors: IdValue<MapSensorDto> list)
        (withEmpty: bool)
        =
        let sensorOptions = List.map sensorToOption availableSensors

        let options =
            if withEmpty then
                option [ Value "" ] [ str "-" ] :: sensorOptions
            else
                sensorOptions

        let findSensor name =
            availableSensors
            |> List.tryFind (fun sensor -> (MapSensor.getBaseData sensor.Value).Name = name)

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(
                        selected
                        |> Option.map (fun sensor -> (MapSensor.getBaseData sensor.Value).Name)
                        |> Option.defaultValue ""
                    )
                    OnChange(fun event -> (event.Value |> findSensor |> onSensorSelected))
                ]
                options
        ]

[<RequireQualifiedAccess>]
module MapSensorTypeFilter =
    let sensorTypeToOption (mapSensorType: MapSensorType) =
        let sensorType = MapSensorType.toString mapSensorType

        option [ Value sensorType ] [ str sensorType ]

    let view
        (availableTypes: MapSensorType list)
        (selected: MapSensorType option)
        (onSelect: MapSensorType option -> unit)
        =
        let options = [
            option [ Value "" ] [ str "-" ]

            yield! availableTypes |> List.map sensorTypeToOption
        ]

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    Value(selected |> Option.map MapSensorType.toString |> Option.defaultValue "")
                    OnChange(fun event -> event.Value |> MapSensorType.fromString |> onSelect)
                ]
                options
        ]

[<RequireQualifiedAccess>]
module MapSensorTypeSelect =
    let sensorTypeToOption (mapSensorType: MapSensorType) =
        let sensorType = MapSensorType.toString mapSensorType

        option [ Value sensorType ] [ str sensorType ]

    let view
        (isDisabled: bool)
        (availableTypes: MapSensorType list)
        (onSelect: MapSensorType -> unit)
        (selected: MapSensorType)
        =
        let options =
            availableTypes
            |> List.sortBy MapSensorType.toString
            |> List.map sensorTypeToOption

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(MapSensorType.toString selected)
                    HTMLAttr.Disabled isDisabled
                    OnChange(fun event ->
                        event.Value
                        |> MapSensorType.fromString
                        |> Option.defaultValue availableTypes[0]
                        |> onSelect
                    )
                ]
                options
        ]